import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { useEffect, useState } from "react";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ButtonGroup,
  ConfirmButton,
  Row,
  CancelButton,
  InputContainer,
  Input,
  Label,
  TemplateListContainer,
  TemplateItem,
  TemplateInfo,
  TemplateName,
  TemplateAuthor,
  TemplateActions,
  IconButton,
} from "./EditPlaylistModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevron-down.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete.svg";
import { useDispatch, useSelector } from "react-redux";
import { editPlaylist } from "../../store/slices/playlist/asyncThunk";
import { toast } from "sonner";
import {
  selectPlaylist,
  selectPlaylistPending,
} from "../../store/slices/playlist/slice";

const EditPlaylistModal = ({ isOpen, onClose }) => {
  useLockBodyScroll(isOpen);

  const { data: playlist } = useSelector(selectPlaylist);

  const dispatch = useDispatch();
  const [playlistName, setPlaylistName] = useState(playlist?.name || "");
  const [templates, setTemplates] = useState(playlist?.collections || []);

  const isLoading = useSelector(selectPlaylistPending).editPlaylist;

  useEffect(() => {
    if (playlist) {
      setPlaylistName(playlist?.name || "");
      setTemplates(playlist?.collections || []);
    }
  }, [playlist]);

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newTemplates = [...templates];
    [newTemplates[index - 1], newTemplates[index]] = [
      newTemplates[index],
      newTemplates[index - 1],
    ];
    setTemplates(newTemplates);
  };

  const handleMoveDown = (index) => {
    if (index === templates.length - 1) return;
    const newTemplates = [...templates];
    [newTemplates[index], newTemplates[index + 1]] = [
      newTemplates[index + 1],
      newTemplates[index],
    ];
    setTemplates(newTemplates);
  };

  const handleDelete = (templateId) => {
    setTemplates((prev) => prev.filter((t) => t._id !== templateId));
  };

  const handleConfirm = () => {
    const collections = templates.map((t) => t._id);

    dispatch(
      editPlaylist({
        name: playlistName,
        collections,
        playlistId: playlist?._id,
      }),
    )
      .unwrap()
      .then(() => {
        onClose();
        toast.success("Playlist edited successfully.");
      })
      .catch(() => {
        onClose();
      });
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const isConfirmDisabled = isLoading || !playlistName.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3600 }}
    >
      <ModalContainer>
        <ModalContent>
          <Row>
            <ModalTitle>Edit Playlist</ModalTitle>
            <CloseButton onClick={handleCancel}>
              <CloseSvg width={20} height={20} />
            </CloseButton>
          </Row>

          <InputContainer>
            <Label>Playlist Title</Label>
            <Input
              value={playlistName}
              onChange={(e) => setPlaylistName(e.target.value)}
              placeholder="Enter playlist name here"
            />
          </InputContainer>

          <InputContainer>
            <Label>Templates ({templates.length})</Label>
            <TemplateListContainer $paddingRight={templates.length >= 5}>
              {templates.map((template, index) => (
                <TemplateItem key={template._id}>
                  <TemplateInfo>
                    <TemplateName>{template?.name}</TemplateName>
                    <TemplateAuthor>@{template?.username}</TemplateAuthor>
                  </TemplateInfo>
                  <TemplateActions>
                    <IconButton
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      $rotate
                    >
                      <ChevronIcon width={12} height={12} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleMoveDown(index)}
                      disabled={index === templates.length - 1}
                    >
                      <ChevronIcon width={12} height={12} />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(template._id)}>
                      <DeleteIcon width={16} height={16} />
                    </IconButton>
                  </TemplateActions>
                </TemplateItem>
              ))}
            </TemplateListContainer>
          </InputContainer>

          <ButtonGroup>
            <CancelButton onClick={handleCancel} disabled={isLoading}>
              Cancel
            </CancelButton>
            <ConfirmButton
              onClick={handleConfirm}
              $type="primary"
              disabled={isConfirmDisabled}
            >
              {isLoading ? (
                <l-line-spinner size="20" stroke="2" speed="1" color="#000" />
              ) : (
                "Save"
              )}
            </ConfirmButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default EditPlaylistModal;
